import type { FC, ReactNode } from 'react';
import React, { createContext, useCallback, useContext } from 'react';
import type { BackOfficeExtensionContainer } from '@wix/extensions-data-client';
import { useExtensionDataClient } from '@wix/extensions-data-client';
import { Spinner, useAsync } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { isType, match } from 'variant';
import { useErrorReporter } from '@wix/cli-error-reporting';
import type { DashboardExtension } from '../components/extensions/useExtensions.js';
import { useExtensions } from '../components/extensions/useExtensions.js';
import type { DevCenterComponent } from '../dev-center/components/dev-center-component.js';

interface ExtensionDataContextProps {
  getDashboardExtensionsRoute: (
    dashboardExtension: DevCenterComponent<DashboardExtension>
  ) => string;
}

const ExtensionDataContext = createContext<{
  extensionDataContainers: Record<string, BackOfficeExtensionContainer>;
} | null>(null);

interface ExtensionDataProviderProps {
  children: ReactNode;
}

export const ExtensionDataProvider: FC<ExtensionDataProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { hasExtensions } = useExtensions();
  const { reportError } = useErrorReporter();
  const { getBackofficeExtensionContainers } = useExtensionDataClient();

  const { status } = useAsync(async () => {
    if (
      hasExtensions('DashboardMenuPlugin') ||
      hasExtensions('DashboardPlugin')
    ) {
      const backofficeExtensionContainers =
        await getBackofficeExtensionContainers().catch((error: unknown) => {
          reportError(error);
          return [];
        });

      return backofficeExtensionContainers.reduce<
        Record<string, BackOfficeExtensionContainer>
      >((extensionContainersMap, backofficeExtensionContainer) => {
        extensionContainersMap[
          backofficeExtensionContainer.component.componentId
        ] = backofficeExtensionContainer;
        return extensionContainersMap;
      }, {});
    }

    return {};
  }, [hasExtensions]);

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: extensionDataContainers }) => (
      <ExtensionDataContext.Provider value={{ extensionDataContainers }}>
        {children}
      </ExtensionDataContext.Provider>
    ),
  });
};

export function useExtensionData(): ExtensionDataContextProps {
  const context = useContext(ExtensionDataContext);

  if (context == null) {
    throw new Error(
      'useExtensionData() cannot be used without an <ExtensionDataProvider>'
    );
  }

  const getDashboardExtensionsRoute = useCallback(
    (dashboardExtension: DevCenterComponent<DashboardExtension>) => {
      if (
        isType(dashboardExtension, 'DashboardPlugin') ||
        isType(dashboardExtension, 'DashboardMenuPlugin')
      ) {
        return (
          context.extensionDataContainers[dashboardExtension.extends]?.component
            .data.backOfficeExtensionContainer.defaultRoute ?? '/'
        );
      }
      return dashboardExtension.route;
    },
    [context]
  );

  return { getDashboardExtensionsRoute };
}
